import { FC } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import {
  Box,
  Flex,
  GreyGrey100,
  Primary13SemiBold,
  TextAreaField
} from '@sequencehq/core-components'

import { QuoteRecipientsNotifyList } from 'Cube/view/layouts/quote/components/QuoteRecipientsNotifyList'
import { PUBLISH_MODAL_COPY } from './PublishQuoteModalV2.constants'
import type { PublishRecipients } from './usePublishRecipients'

export const RECIPIENTS_SECTION_TEST_ID = 'modal.publishQuote.recipients'

type Props = {
  recipients: PublishRecipients
  message: string
  setMessage: (message: string) => void
  submitting: boolean
}

export const RecipientsSection: FC<Props> = ({
  recipients,
  message,
  setMessage,
  submitting
}) => {
  const hasSelection = recipients.emails.length > 0

  // Nobody to share with, so an empty bordered box would only ask a question
  // the quote can't answer.
  if (recipients.rows.length === 0) {
    return null
  }

  return (
    <Flex flexDir="column" gap="8px">
      <Box
        {...Primary13SemiBold}
        color={GreyGrey100}
        data-testid={`${RECIPIENTS_SECTION_TEST_ID}.heading`}
      >
        {PUBLISH_MODAL_COPY.recipients.heading}
      </Box>

      <QuoteRecipientsNotifyList
        data-testid={RECIPIENTS_SECTION_TEST_ID}
        rows={recipients.rows}
        selectedIds={recipients.selectedIds}
        onSelectionChange={recipients.setSelectedIds}
        canEditRecipientType
        onTypeChange={recipients.onTypeChange}
        showCounterSignerLastNote={recipients.showCounterSignerLastNote}
        signersUnavailableNote={recipients.signersUnavailableNote}
        disabled={submitting}
      />

      <AnimatePresence initial={false}>
        {hasSelection && (
          <motion.div
            initial={{ height: '0px' }}
            animate={{ height: 'fit-content' }}
            exit={{ height: '0px' }}
            style={{ overflow: 'hidden' }}
          >
            {/* The container clips to animate its height, so the field needs
                room or its focus ring is shaved off at the bottom. */}
            <Box paddingTop="8px" paddingBottom="2px">
              <TextAreaField
                data-testid={`${RECIPIENTS_SECTION_TEST_ID}.message`}
                label={PUBLISH_MODAL_COPY.recipients.messageLabel}
                placeholder={PUBLISH_MODAL_COPY.recipients.messagePlaceholder}
                value={message}
                onChange={value => setMessage(value)}
                isDisabled={submitting}
                minHeight="100px"
                styles={{ label: { color: GreyGrey100 } }}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>
  )
}
